<template>
  <div :class="[{ isBanner }, isOpen ? 'open' : 'closed']" data-qe-id="lobby-status">
    <p v-if="isOpen" class="lobby-status-text">
      <span class="icon" />{{ $t('zoneSelection.lobbyStatusBanner.isOpen') }}
    </p>
    <p v-if="willOpen" class="lobby-status-text">
      <span class="icon" />{{ $t('zoneSelection.lobbyStatusBanner.willOpen', { openingTime: countdownTxt }) }}
    </p>
    <p v-if="isClosed" class="lobby-status-text">
      <span class="icon" />{{ $t('zoneSelection.lobbyStatusBanner.isClosed') }}
    </p>
  </div>
</template>

<script setup lang="ts">
import { watch, ref, computed } from 'vue'
import { LobbyTimerUi, LobbyTimersUi } from '@/model/LobbyTimers'
import { useI18n } from 'vue-i18n'

const props = withDefaults(defineProps<{ timers: LobbyTimersUi; isBanner?: boolean }>(), { isBanner: false })
const emit = defineEmits(['lobby-timer-complete'])
const { t } = useI18n()
const countdownTxt = ref('')

watch(
  () => props.timers,
  (newVal: LobbyTimersUi, oldVal: LobbyTimersUi) => {
    const time = props.timers.start

    countdownTxt.value = `${getDays(time)} ${getHours(time)} ${getMinutes(time)} ${getSeconds(time)}`

    if (
      oldVal &&
      ((newVal.start.totalSecs === 0 && newVal.start.totalSecs !== oldVal.start.totalSecs) ||
        (newVal.end.totalSecs === 0 && newVal.end.totalSecs !== oldVal.end.totalSecs))
    ) {
      setTimeout(() => {
        emit('lobby-timer-complete')
      }, 1000)
    }
  },
  { immediate: true, deep: true }
)

const isOpen = computed(() => {
  return props.timers.start.totalSecs === 0 && props.timers.end.totalSecs > 0
})

const willOpen = computed(() => {
  return props.timers.start.totalSecs > 0
})

const isClosed = computed(() => {
  return props.timers.start.totalSecs <= 0 && props.timers.end.totalSecs <= 0
})

/**
 * Days string
 * plus adding grammatic syntax sugar on top (comma or coordinating conjunction)
 */
function getDays(time: LobbyTimerUi): string {
  let daysStr = t('zoneSelection.lobbyStatusBanner.countdown_days', time.days)
  if (time.days > 0) {
    if (time.hours > 0 && time.minutes > 0) {
      daysStr += ', '
    } else if (time.hours > 0 || time.minutes > 0) {
      daysStr += ` ${t('zoneSelection.lobbyStatusBanner.countdown_and')} `
    }
  }
  return time.days > 0 ? daysStr : ''
}

/**
 * Hours string
 * plus adding grammatic syntax sugar on top (comma or coordinating conjunction)
 */
function getHours(time: LobbyTimerUi): string {
  let hoursStr = t('zoneSelection.lobbyStatusBanner.countdown_hours', time.hours)
  if (time.hours > 0) {
    if (time.minutes > 0) {
      hoursStr += ` ${t('zoneSelection.lobbyStatusBanner.countdown_and')} `
    }
  } else {
    hoursStr += ', '
  }
  return time.hours > 0 ? hoursStr : ''
}

/**
 * Minutes string
 * plus adding grammatic syntax sugar on top (comma or coordinating conjunction)
 */
function getMinutes(time: LobbyTimerUi): string {
  let minuteStr = t('zoneSelection.lobbyStatusBanner.countdown_minutes', time.minutes)
  if (time.days <= 0 && time.hours <= 0 && time.minutes < 5 && time.seconds > 0) {
    minuteStr += ` ${t('zoneSelection.lobbyStatusBanner.countdown_and')} `
  }
  return time.minutes > 0 ? minuteStr : ''
}

/**
 * Seconds string
 * plus adding grammatic syntax sugar on top (comma or coordinating conjunction)
 */
function getSeconds(time: LobbyTimerUi): string {
  let seconds = ''
  if (time.days <= 0 && time.hours <= 0 && time.minutes < 5 && time.seconds > 0) {
    seconds = t('zoneSelection.lobbyStatusBanner.countdown_seconds', time.seconds)
  }
  return seconds
}
</script>

<style lang="less" scoped>
@import '~@npm/@gd/common-styles/lib/color-vars';
@import '~@npm/@gd/common-styles/lib/font';
@import '../styles/vars';

.isBanner {
  display: inline-block;
  position: relative;
  font-weight: 700;
  font-size: 0.875rem;
  z-index: 10;
  .lobby-status-text {
    font-size: 1em;
  }
  &:before {
    content: '';
    position: absolute;
    border-style: solid;
    top: 0.5em;
    left: -1.8em;
    border-width: 1.1em 1em 1.1em 1.1em;
    border-color: white white white transparent;
    filter: drop-shadow(0 2px 2px rgba(0, 0, 0, 0.5));
    z-index: -1;
  }
  &:after {
    content: '';
    position: absolute;
    border-style: solid;
    top: 0.5em;
    right: -1.8em;
    border-width: 1.1em 1em 1.1em 1.1em;
    border-color: white transparent white white;
    filter: drop-shadow(0 2px 2px rgba(0, 0, 0, 0.5));
    z-index: -1;
  }
  .lobby-status-text {
    display: inline-block;
    position: relative;
    padding: 0.5em 0.8em;
    background-color: white;
    background-repeat: no-repeat;
    box-shadow: 0 2px 3px rgba(0, 0, 0, 0.3);
    z-index: 10;
    &:before {
      content: ' ';
      position: absolute;
      bottom: -0.4em;
      left: 0;
      width: 0;
      height: 0;
      border-style: solid;
      border-width: 0 0.4em 0.4em 0;
      border-color: transparent #333 transparent transparent;
    }
    &:after {
      content: ' ';
      position: absolute;
      bottom: -0.4em;
      right: 0;
      width: 0;
      height: 0;
      border-style: solid;
      border-width: 0.4em 0.4em 0 0;
      border-color: #333 transparent transparent transparent;
    }
    &.open {
      color: @lobbyStatus--open;
      background-image: url('../assets/icon_lock_open.svg');
      background-size: 1.25em;
      background-position: 0.6em 0.5em;
    }
    &.closed {
      color: @lobbyStatus--closed;
      background-image: url('../assets/icon_lock_closed.svg');
      background-size: 1em;
      background-position: 0.75em 0.5em;
    }
  }
}
.lobby-status-text {
  font-family: @defaultFontFamily;
  margin: 0;
  font-size: 0.75rem;
  font-weight: 700;
  .icon {
    display: inline-block;
    height: 1rem;
  }
  .open & {
    color: @lobbyStatus--open;
    .icon {
      padding-left: 1.625rem;
      background: url('../assets/icon_lock_open.svg') 0 0 no-repeat;
    }
  }
  .closed & {
    color: @lobbyStatus--closed;
    .icon {
      padding-left: 1.25rem;
      background: url('../assets/icon_lock_closed.svg') 0 0 no-repeat;
    }
  }
}
</style>
